import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Icon } from '@lnr-v2/ui';
import { AdminLayout } from '../../layouts/AdminLayout';
import { TranslationItem } from '../../components/organisms/TranslationItem';
import { TranslationCreateModal } from '../../components/organisms/TranslationCreateModal';
import { Button } from '../../components/atoms/Button';
import { Spinner } from '../../components/atoms/Spinner';
import { ErrorMessage } from '../../components/atoms/ErrorMessage';
import {
  useTranslationsByPage,
  useTranslationStats,
} from '../../hooks/useTranslations';
import type { Translation } from '../../services/translations.service';
import styles from './TranslationsPage.module.css';

type PageTab = 'home' | 'hub-cases' | 'cases' | 'common';

export const TranslationsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const filterParam = searchParams.get('filter');

  const [activePage, setActivePage] = useState<PageTab>('home');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set()
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Apply filter on mount if URL param exists
  useEffect(() => {
    if (filterParam) {
      // Extract page from filter (e.g., "cases.digital-transformation" -> "cases")
      const page = filterParam.split('.')[0];
      if (page === 'home' || page === 'cases' || page === 'common') {
        setActivePage(page);
      }
      // Set search query to the full filter
      setSearchQuery(filterParam);

      // Auto-expand the section if it matches the filter pattern
      // e.g., "cases.digital-transformation" -> expand "digital-transformation"
      const parts = filterParam.split('.');
      if (parts.length >= 2 && parts[1]) {
        const sectionName = parts[1];
        setExpandedSections(new Set([sectionName]));
      }
    }
  }, [filterParam]);

  // Fetch data
  const {
    data: translations,
    isLoading,
    error,
  } = useTranslationsByPage(activePage);
  const { data: stats } = useTranslationStats();

  // Group translations by section
  const translationsBySection = useMemo(() => {
    if (!translations) return {};

    return translations.reduce(
      (acc, translation) => {
        const section = translation.section;
        if (!acc[section]) {
          acc[section] = [];
        }
        acc[section].push(translation);
        return acc;
      },
      {} as Record<string, Translation[]>
    );
  }, [translations]);

  // Filter translations by search query
  const filteredTranslations = useMemo(() => {
    if (!searchQuery.trim()) return translationsBySection;

    const query = searchQuery.toLowerCase();
    const filtered: Record<string, Translation[]> = {};

    Object.entries(translationsBySection).forEach(([section, items]) => {
      const matchingItems = items.filter(
        (t) =>
          t.label.toLowerCase().includes(query) ||
          t.textEN.toLowerCase().includes(query) ||
          t.textFR.toLowerCase().includes(query) ||
          t.section.toLowerCase().includes(query) ||
          t.element.toLowerCase().includes(query)
      );

      if (matchingItems.length > 0) {
        filtered[section] = matchingItems;
      }
    });

    return filtered;
  }, [translationsBySection, searchQuery]);

  // Page tabs configuration
  const pageTabs = [
    {
      id: 'home' as PageTab,
      label: 'Home Page',
      icon: 'home',
      count: stats?.byPage.find((p) => p.page === 'home')?.count || 0,
    },
    {
      id: 'hub-cases' as PageTab,
      label: 'Hub Cases Page',
      icon: 'folder',
      count: stats?.byPage.find((p) => p.page === 'hub-cases')?.count || 0,
    },
    {
      id: 'cases' as PageTab,
      label: 'Cases Page',
      icon: 'folder',
      count: stats?.byPage.find((p) => p.page === 'cases')?.count || 0,
    },
    {
      id: 'common' as PageTab,
      label: 'Common',
      icon: 'globe',
      count: stats?.byPage.find((p) => p.page === 'common')?.count || 0,
    },
  ];

  // Toggle section expand/collapse
  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  // Expand all sections
  const expandAll = () => {
    setExpandedSections(new Set(Object.keys(translationsBySection)));
  };

  // Collapse all sections
  const collapseAll = () => {
    setExpandedSections(new Set());
  };

  // Format section name for display
  const formatSectionName = (section: string): string => {
    return section
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <AdminLayout>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>Translations</h1>
            <p className={styles.subtitle}>
              Manage website translations for English and French
            </p>
          </div>
          <div className={styles.headerRight}>
            {stats && (
              <div className={styles.statBadge}>
                <Icon name="globe" size="sm" color="currentColor" />
                <span>{stats.total} translations</span>
              </div>
            )}
            <Button
              variant="primary"
              size="md"
              onClick={() => setIsCreateModalOpen(true)}
              leftIcon={<Icon name="plus" size="sm" color="currentColor" />}
            >
              Create Translation
            </Button>
          </div>
        </div>

        {/* Page tabs */}
        <div className={styles.tabs}>
          {pageTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActivePage(tab.id)}
              className={`${styles.tab} ${
                activePage === tab.id ? styles.tabActive : ''
              }`}
            >
              <Icon name={tab.icon} size="sm" color="currentColor" />
              <span className={styles.tabLabel}>{tab.label}</span>
              <span className={styles.tabCount}>{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <Icon name="search" size="sm" color="var(--color-text-tertiary)" />
            <input
              type="text"
              placeholder="Search translations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className={styles.clearButton}
                title="Clear search"
              >
                <Icon name="x" size="sm" color="currentColor" />
              </button>
            )}
          </div>

          <div className={styles.toolbarActions}>
            <button onClick={expandAll} className={styles.toolbarButton}>
              <Icon name="chevron-down" size="sm" color="currentColor" />
              Expand All
            </button>
            <button onClick={collapseAll} className={styles.toolbarButton}>
              <Icon name="chevron-up" size="sm" color="currentColor" />
              Collapse All
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {isLoading && (
            <div className={styles.loadingState}>
              <Spinner size="lg" />
              <p>Loading translations...</p>
            </div>
          )}

          {error && (
            <ErrorMessage
              message={
                error instanceof Error
                  ? error.message
                  : 'Failed to load translations'
              }
            />
          )}

          {!isLoading && !error && translations && (
            <>
              {Object.keys(filteredTranslations).length === 0 && (
                <div className={styles.emptyState}>
                  <Icon
                    name="search"
                    size="lg"
                    color="var(--color-text-tertiary)"
                  />
                  <p>
                    {searchQuery
                      ? 'No translations match your search'
                      : 'No translations found for this page'}
                  </p>
                </div>
              )}

              {Object.entries(filteredTranslations).map(
                ([section, sectionTranslations]) => {
                  const isExpanded = expandedSections.has(section);

                  return (
                    <div key={section} className={styles.section}>
                      {/* Section header */}
                      <button
                        onClick={() => toggleSection(section)}
                        className={styles.sectionHeader}
                      >
                        <div className={styles.sectionTitle}>
                          <Icon
                            name={isExpanded ? 'chevron-down' : 'chevron-right'}
                            size="sm"
                            color="currentColor"
                          />
                          <h3>{formatSectionName(section)}</h3>
                          <span className={styles.sectionCount}>
                            {sectionTranslations.length}
                          </span>
                        </div>
                      </button>

                      {/* Section content */}
                      {isExpanded && (
                        <div className={styles.sectionContent}>
                          {sectionTranslations.map((translation) => (
                            <TranslationItem
                              key={translation.id}
                              translation={translation}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
              )}
            </>
          )}
        </div>

        {/* Create Translation Modal */}
        <TranslationCreateModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          currentPage={activePage}
        />
      </div>
    </AdminLayout>
  );
};
